import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Youtube } from '@tiptap/extension-youtube';
import { toast } from 'react-toastify';
import Modal from '../components/common/Modal';
import styles from './ContentManagementPage.module.css';
import ApiClient from '../services/ApiClient';

const FONT_SIZES = [
  { label: 'Varsayılan', value: '' },
  { label: '12px', value: '12px' },
  { label: '14px', value: '14px' },
  { label: '16px', value: '16px' },
  { label: '18px', value: '18px' },
  { label: '20px', value: '20px' },
  { label: '24px', value: '24px' },
  { label: '28px', value: '28px' },
  { label: '32px', value: '32px' },
  { label: '36px', value: '36px' },
  { label: '48px', value: '48px' },
];

const COLOR_PALETTE = [
  '#000000', '#434343', '#666666', '#999999',
  '#DC2626', '#EA580C', '#D97706', '#16A34A',
  '#2563EB', '#7C3AED', '#DB2777', '#0891B2',
];

const ContentManagementPage = () => {
  const [activeTab, setActiveTab] = useState('about');
  const [selectedLanguage, setSelectedLanguage] = useState('tr');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPages, setLoadingPages] = useState({
    about: false,
    privacy: false,
    contact: false
  });
  const [error, setError] = useState(null);
  const [savedContent, setSavedContent] = useState({
    about: '',
    privacy: '',
    contact: ''
  });

  // Source mode
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');

  // Link input bar
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Image modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  // YouTube modal
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');

  // Color palette popovers
  const [showTextColorPalette, setShowTextColorPalette] = useState(false);
  const [showHighlightPalette, setShowHighlightPalette] = useState(false);
  const textColorRef = useRef(null);
  const highlightRef = useRef(null);

  // Dil seçenekleri
  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'İçeriğinizi buraya yazın...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Youtube.configure({
        inline: false,
        ccLanguage: 'tr',
      }),
    ],
    content: savedContent[activeTab],
    onUpdate: () => {},
  });

  // Close color palettes on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (textColorRef.current && !textColorRef.current.contains(e.target)) {
        setShowTextColorPalette(false);
      }
      if (highlightRef.current && !highlightRef.current.contains(e.target)) {
        setShowHighlightPalette(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update editor content when tab changes or language changes
  useEffect(() => {
    if (editor) {
      const content = savedContent[activeTab];
      editor.commands.setContent(content);
      if (isSourceMode) {
        setHtmlSource(content);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedLanguage, editor, savedContent]);

  // Load content from API
  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const loadContent = async () => {
    setIsLoading(true);
    setError(null);

    setLoadingPages({
      about: true,
      privacy: true,
      contact: true
    });

    const newContent = {
      about: '',
      privacy: '',
      contact: ''
    };

    try {
      const pages = ['about', 'privacy', 'contact'];

      for (const page of pages) {
        try {
          const response = await ApiClient.get(`/static-pages/${page}?language=${selectedLanguage}`);
          newContent[page] = response.data.data?.content || '';
        } catch (pageError) {
          console.error(`${page} sayfası yüklenirken hata:`, pageError);
          newContent[page] = '';
        } finally {
          setLoadingPages(prev => ({
            ...prev,
            [page]: false
          }));
        }
      }

      setSavedContent(newContent);
    } catch (error) {
      console.error('İçerik yüklenirken genel hata:', error);
      setError('İçerik yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      setSavedContent(newContent);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentContent = useCallback(() => {
    if (isSourceMode) {
      return htmlSource;
    }
    return editor ? editor.getHTML() : '';
  }, [isSourceMode, htmlSource, editor]);

  const handleSave = async () => {
    if (!editor && !isSourceMode) return;

    setIsLoading(true);
    try {
      const content = getCurrentContent();

      const response = await ApiClient.put(`/static-pages/${activeTab}`, {
        language: selectedLanguage,
        content: content
      });

      const returnedContent = response.data.data?.content || content;

      setSavedContent(prev => ({
        ...prev,
        [activeTab]: returnedContent
      }));

      toast.success('İçerik başarıyla kaydedildi!');
    } catch (error) {
      console.error('İçerik kaydedilirken hata:', error);
      toast.error('İçerik kaydedilirken bir hata oluştu!');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    const content = getCurrentContent();
    const pageTitles = {
      about: { tr: 'Hakkında', de: 'Über uns', en: 'About' },
      privacy: { tr: 'Gizlilik Politikası', de: 'Datenschutz', en: 'Privacy Policy' },
      contact: { tr: 'İletişim', de: 'Kontakt', en: 'Contact' }
    };

    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <!DOCTYPE html>
      <html lang="${selectedLanguage}">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Utkan eSIM - ${pageTitles[activeTab][selectedLanguage]}</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
          <style>
              * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
              }

              body {
                  font-family: 'Poppins', sans-serif;
                  color: #e2e8f0;
                  line-height: 1.6;
              }

              .static-page-wrapper {
                  min-height: 100vh;
                  background: linear-gradient(135deg, #0a0f1c 0%, #1e293b 50%, #0f172a 100%);
                  font-family: 'Poppins', sans-serif;
                  color: #e2e8f0;
                  padding: 20px;
                  padding-top: 90px;
                  box-sizing: border-box;
              }

              .static-container {
                  max-width: 1200px;
                  margin: 0 auto;
                  background: rgba(15, 23, 42, 0.7);
                  border: 1px solid rgba(59, 130, 246, 0.2);
                  backdrop-filter: blur(20px);
                  -webkit-backdrop-filter: blur(20px);
                  border-radius: 30px;
                  padding: 50px;
                  box-shadow:
                      0 20px 60px rgba(0, 0, 0, 0.4),
                      0 0 0 1px rgba(59, 130, 246, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1);
              }

              .static-container table {
                  border-collapse: collapse;
                  width: 100%;
                  margin: 16px 0;
              }

              .static-container td,
              .static-container th {
                  border: 1px solid rgba(59, 130, 246, 0.3);
                  padding: 12px;
                  text-align: left;
              }

              .static-container th {
                  background: rgba(59, 130, 246, 0.15);
                  font-weight: 600;
              }

              .static-container img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 8px;
                  margin: 16px 0;
              }

              .static-container iframe {
                  max-width: 100%;
                  border-radius: 8px;
                  margin: 16px 0;
              }

              .static-container blockquote {
                  border-left: 4px solid #3b82f6;
                  padding: 12px 20px;
                  margin: 16px 0;
                  background: rgba(59, 130, 246, 0.05);
                  border-radius: 0 8px 8px 0;
                  font-style: italic;
                  color: #cbd5e1;
              }

              .static-container hr {
                  border: none;
                  height: 1px;
                  background: rgba(59, 130, 246, 0.3);
                  margin: 24px 0;
              }

              .static-container pre {
                  background: rgba(0, 0, 0, 0.4);
                  border: 1px solid rgba(59, 130, 246, 0.2);
                  border-radius: 8px;
                  padding: 16px;
                  overflow-x: auto;
                  margin: 16px 0;
              }

              .static-container code {
                  font-family: 'Fira Code', monospace;
                  font-size: 0.9em;
              }

              .hero-section {
                  text-align: center;
                  margin-bottom: 60px;
                  padding-bottom: 40px;
                  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
              }

              .gradient-title {
                  font-size: 3.5rem;
                  font-weight: 800;
                  margin: 0 0 20px 0;
                  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%);
                  -webkit-background-clip: text;
                  -webkit-text-fill-color: transparent;
                  background-clip: text;
                  animation: gradientShift 3s ease-in-out infinite;
              }

              @keyframes gradientShift {
                  0%, 100% { filter: hue-rotate(0deg); }
                  50% { filter: hue-rotate(30deg); }
              }

              .hero-description {
                  font-size: 1.3rem;
                  color: #cbd5e1;
                  line-height: 1.8;
                  max-width: 800px;
                  margin: 0 auto 30px auto;
              }

              .section-header {
                  display: flex;
                  align-items: center;
                  gap: 15px;
                  margin-bottom: 25px;
              }

              .section-icon {
                  font-size: 2rem;
                  color: #3b82f6;
                  background: rgba(59, 130, 246, 0.1);
                  padding: 15px;
                  border-radius: 15px;
                  border: 1px solid rgba(59, 130, 246, 0.2);
              }

              .section-header h2 {
                  font-size: 2rem;
                  font-weight: 700;
                  color: #f1f5f9;
                  margin: 0;
              }

              .story-section {
                  margin: 40px 0;
                  padding: 30px;
                  background: rgba(59, 130, 246, 0.05);
                  border-radius: 20px;
                  border: 1px solid rgba(59, 130, 246, 0.1);
              }

              .story-section h2 {
                  font-size: 2rem;
                  font-weight: 700;
                  color: #f1f5f9;
                  margin-bottom: 20px;
              }

              .story-section p {
                  font-size: 1.1rem;
                  line-height: 1.8;
                  color: #cbd5e1;
              }

              .mission-vision {
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 30px;
                  margin: 40px 0;
              }

              .section {
                  padding: 30px;
                  background: rgba(59, 130, 246, 0.05);
                  border-radius: 20px;
                  border: 1px solid rgba(59, 130, 246, 0.1);
                  text-align: center;
                  transition: all 0.3s ease;
              }

              .section:hover {
                  transform: translateY(-5px);
                  background: rgba(59, 130, 246, 0.1);
                  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.2);
              }

              .section h3 {
                  font-size: 1.5rem;
                  font-weight: 600;
                  color: #f1f5f9;
                  margin: 20px 0 15px 0;
              }

              .section p {
                  font-size: 1rem;
                  line-height: 1.7;
                  color: #cbd5e1;
              }

              .features-section {
                  margin: 40px 0;
              }

              .features-section h2 {
                  font-size: 2rem;
                  font-weight: 700;
                  color: #f1f5f9;
                  text-align: center;
                  margin-bottom: 40px;
              }

              .features-grid {
                  display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                  gap: 25px;
              }

              .feature-card {
                  padding: 25px;
                  background: rgba(59, 130, 246, 0.05);
                  border-radius: 15px;
                  border: 1px solid rgba(59, 130, 246, 0.1);
                  text-align: center;
                  transition: all 0.3s ease;
              }

              .feature-card:hover {
                  transform: translateY(-5px);
                  background: rgba(59, 130, 246, 0.1);
                  box-shadow: 0 10px 25px rgba(59, 130, 246, 0.2);
              }

              .feature-icon {
                  font-size: 2.5rem;
                  color: #3b82f6;
                  margin-bottom: 15px;
              }

              .feature-card h4 {
                  font-size: 1.2rem;
                  font-weight: 600;
                  color: #f1f5f9;
                  margin-bottom: 10px;
              }

              .feature-card p {
                  font-size: 0.95rem;
                  color: #cbd5e1;
                  line-height: 1.6;
              }

              .cta-section {
                  text-align: center;
                  margin: 60px 0 0 0;
                  padding: 40px;
                  background: rgba(59, 130, 246, 0.05);
                  border-radius: 20px;
                  border: 1px solid rgba(59, 130, 246, 0.1);
              }

              .cta-section h3 {
                  font-size: 2rem;
                  font-weight: 700;
                  color: #f1f5f9;
                  margin-bottom: 15px;
              }

              .cta-section p {
                  font-size: 1.1rem;
                  color: #cbd5e1;
                  margin-bottom: 30px;
              }

              .cta-buttons {
                  display: flex;
                  justify-content: center;
                  gap: 20px;
                  flex-wrap: wrap;
              }

              .cta-button {
                  display: inline-flex;
                  align-items: center;
                  gap: 10px;
                  padding: 15px 30px;
                  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                  color: white;
                  text-decoration: none;
                  border-radius: 25px;
                  font-weight: 600;
                  transition: all 0.3s ease;
                  border: 1px solid rgba(59, 130, 246, 0.3);
              }

              .cta-button:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
              }

              .cta-button.whatsapp:hover {
                  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
              }

              .cta-button.email:hover {
                  background: linear-gradient(135deg, #ea4335 0%, #d93025 100%);
              }

              @media (max-width: 768px) {
                  .static-page-wrapper {
                      padding-top: 20px;
                  }
                  .static-container {
                      padding: 30px 20px;
                      border-radius: 20px;
                  }
                  .gradient-title {
                      font-size: 2.5rem;
                  }
                  .hero-description {
                      font-size: 1.1rem;
                  }
                  .section-header {
                      flex-direction: column;
                      text-align: center;
                  }
                  .section-header h2 {
                      font-size: 1.5rem;
                  }
                  .section-icon {
                      font-size: 1.5rem;
                      padding: 10px;
                  }
                  .story-section {
                      padding: 20px;
                  }
                  .story-section h2 {
                      font-size: 1.5rem;
                  }
                  .story-section p {
                      font-size: 1rem;
                  }
                  .mission-vision {
                      grid-template-columns: 1fr;
                  }
                  .section {
                      padding: 20px;
                  }
                  .section h3 {
                      font-size: 1.3rem;
                  }
                  .features-section h2 {
                      font-size: 1.5rem;
                  }
                  .features-grid {
                      grid-template-columns: 1fr;
                  }
                  .feature-card {
                      padding: 20px;
                  }
                  .feature-icon {
                      font-size: 2rem;
                  }
                  .feature-card h4 {
                      font-size: 1.1rem;
                  }
                  .cta-section {
                      padding: 30px 20px;
                  }
                  .cta-section h3 {
                      font-size: 1.5rem;
                  }
                  .cta-section p {
                      font-size: 1rem;
                  }
                  .cta-buttons {
                      flex-direction: column;
                      align-items: center;
                  }
                  .cta-button {
                      width: 100%;
                      max-width: 300px;
                      justify-content: center;
                  }
              }

              @media (max-width: 480px) {
                  .static-page-wrapper {
                      padding: 10px;
                  }
                  .static-container {
                      padding: 20px 15px;
                  }
                  .gradient-title {
                      font-size: 2rem;
                  }
                  .hero-description {
                      font-size: 1rem;
                  }
                  .section-header h2 {
                      font-size: 1.3rem;
                  }
                  .story-section h2,
                  .features-section h2,
                  .cta-section h3 {
                      font-size: 1.3rem;
                  }
                  .section h3 {
                      font-size: 1.2rem;
                  }
                  .feature-card h4,
                  .feature-card p,
                  .section p,
                  .story-section p {
                      font-size: 0.95rem;
                  }
                  .feature-icon {
                      font-size: 1.8rem;
                  }
                  .section-icon {
                      font-size: 1.3rem;
                      padding: 8px;
                  }
              }
          </style>
      </head>
      <body>
          <div class="static-page-wrapper">
              <div class="static-container">
                  ${content}
              </div>
          </div>
      </body>
      </html>
    `);
    newWindow.document.close();
  };

  // Source mode toggle
  const toggleSourceMode = () => {
    if (isSourceMode) {
      // Switching from source to WYSIWYG
      if (editor) {
        editor.commands.setContent(htmlSource);
      }
      setIsSourceMode(false);
    } else {
      // Switching from WYSIWYG to source
      if (editor) {
        setHtmlSource(editor.getHTML());
      }
      setIsSourceMode(true);
    }
  };

  // Link handlers
  const handleLinkToggle = () => {
    if (showLinkInput) {
      setShowLinkInput(false);
      setLinkUrl('');
      return;
    }
    const existingUrl = editor?.getAttributes('link').href || '';
    setLinkUrl(existingUrl);
    setShowLinkInput(true);
  };

  const applyLink = () => {
    if (!editor || !linkUrl) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  // Image handler
  const insertImage = () => {
    if (!editor || !imageUrl) return;
    editor.chain().focus().setImage({ src: imageUrl }).run();
    setShowImageModal(false);
    setImageUrl('');
  };

  // YouTube handler
  const insertYoutube = () => {
    if (!editor || !youtubeUrl) return;
    editor.commands.setYoutubeVideo({ src: youtubeUrl });
    setShowYoutubeModal(false);
    setYoutubeUrl('');
  };

  // Font size handler
  const handleFontSizeChange = (e) => {
    const size = e.target.value;
    if (!editor) return;
    if (size === '') {
      editor.chain().focus().unsetFontSize().run();
    } else {
      editor.chain().focus().setFontSize(size).run();
    }
  };

  // Table insert
  const insertTable = () => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const tabs = [
    { id: 'about', label: 'Hakkımızda' },
    { id: 'privacy', label: 'Gizlilik Politikası' },
    { id: 'contact', label: 'İletişim' }
  ];

  const isTableActive = editor?.isActive('table');
  const toolbarDisabled = isSourceMode;

  if (isLoading && !editor) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <p>İçerik yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>İçerik Yönetimi</h1>
        <p>Statik sayfaların içeriklerini düzenleyin</p>
      </div>

      <div className={styles.languageSelector}>
        <label>Dil Seçin:</label>
        <div className={styles.languageButtons}>
          {languages.map(language => (
            <button
              key={language.code}
              className={`${styles.languageButton} ${selectedLanguage === language.code ? styles.activeLanguage : ''}`}
              onClick={() => setSelectedLanguage(language.code)}
            >
              <span className={styles.flag}>{language.flag}</span>
              <span className={styles.languageName}>{language.name}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
          <button onClick={loadContent} className={styles.retryButton}>
            Tekrar Dene
          </button>
        </div>
      )}

      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
            disabled={loadingPages[tab.id]}
          >
            {tab.label}
            {loadingPages[tab.id] && <span className={styles.tabLoading}>...</span>}
          </button>
        ))}
      </div>

      <div className={styles.editorContainer}>
        {/* Toolbar */}
        <div className={`${styles.toolbar} ${toolbarDisabled ? styles.toolbarDisabled : ''}`}>
          {/* Group 1: History */}
          <div className={styles.toolbarGroup}>
            <button
              onClick={() => editor?.chain().focus().undo().run()}
              className={styles.toolbarButton}
              disabled={toolbarDisabled || !editor?.can().undo()}
              title="Geri Al"
            >
              ↩
            </button>
            <button
              onClick={() => editor?.chain().focus().redo().run()}
              className={styles.toolbarButton}
              disabled={toolbarDisabled || !editor?.can().redo()}
              title="Yinele"
            >
              ↪
            </button>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 2: Text formatting */}
          <div className={styles.toolbarGroup}>
            <button
              onClick={() => editor?.chain().focus().toggleBold().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('bold') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Kalın"
            >
              <strong>B</strong>
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('italic') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="İtalik"
            >
              <em>I</em>
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('underline') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Altı Çizili"
            >
              <span style={{ textDecoration: 'underline' }}>U</span>
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('strike') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Üstü Çizili"
            >
              <span style={{ textDecoration: 'line-through' }}>S</span>
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleCode().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('code') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Satır İçi Kod"
            >
              {'<>'}
            </button>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 3: Headings */}
          <div className={styles.toolbarGroup}>
            <button
              onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`${styles.toolbarButton} ${editor?.isActive('heading', { level: 1 }) ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Başlık 1"
            >
              H1
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`${styles.toolbarButton} ${editor?.isActive('heading', { level: 2 }) ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Başlık 2"
            >
              H2
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`${styles.toolbarButton} ${editor?.isActive('heading', { level: 3 }) ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Başlık 3"
            >
              H3
            </button>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 4: Font Size */}
          <div className={styles.toolbarGroup}>
            <select
              className={styles.fontSizeSelect}
              onChange={handleFontSizeChange}
              disabled={toolbarDisabled}
              defaultValue=""
              title="Yazı Boyutu"
            >
              {FONT_SIZES.map(fs => (
                <option key={fs.value} value={fs.value}>{fs.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 5: Colors */}
          <div className={styles.toolbarGroup}>
            <div className={styles.colorPickerWrapper} ref={textColorRef}>
              <button
                className={styles.toolbarButton}
                onClick={() => { setShowTextColorPalette(!showTextColorPalette); setShowHighlightPalette(false); }}
                disabled={toolbarDisabled}
                title="Yazı Rengi"
              >
                <span className={styles.colorButtonLabel}>A</span>
                <span className={styles.colorIndicator} style={{ background: editor?.getAttributes('textStyle').color || '#000000' }} />
              </button>
              {showTextColorPalette && (
                <div className={styles.colorPalette}>
                  {COLOR_PALETTE.map(color => (
                    <button
                      key={color}
                      className={styles.colorSwatch}
                      style={{ background: color }}
                      onClick={() => {
                        editor?.chain().focus().setColor(color).run();
                        setShowTextColorPalette(false);
                      }}
                      title={color}
                    />
                  ))}
                  <button
                    className={styles.colorSwatchClear}
                    onClick={() => {
                      editor?.chain().focus().unsetColor().run();
                      setShowTextColorPalette(false);
                    }}
                    title="Rengi Kaldır"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            <div className={styles.colorPickerWrapper} ref={highlightRef}>
              <button
                className={styles.toolbarButton}
                onClick={() => { setShowHighlightPalette(!showHighlightPalette); setShowTextColorPalette(false); }}
                disabled={toolbarDisabled}
                title="Vurgu Rengi"
              >
                <span className={styles.highlightButtonLabel}>H</span>
                <span className={styles.colorIndicator} style={{ background: editor?.getAttributes('highlight').color || '#FFFF00' }} />
              </button>
              {showHighlightPalette && (
                <div className={styles.colorPalette}>
                  {COLOR_PALETTE.map(color => (
                    <button
                      key={color}
                      className={styles.colorSwatch}
                      style={{ background: color }}
                      onClick={() => {
                        editor?.chain().focus().toggleHighlight({ color }).run();
                        setShowHighlightPalette(false);
                      }}
                      title={color}
                    />
                  ))}
                  <button
                    className={styles.colorSwatchClear}
                    onClick={() => {
                      editor?.chain().focus().unsetHighlight().run();
                      setShowHighlightPalette(false);
                    }}
                    title="Vurguyu Kaldır"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 6: Lists & Alignment */}
          <div className={styles.toolbarGroup}>
            <button
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('bulletList') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Madde İşareti"
            >
              •
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('orderedList') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Numaralı Liste"
            >
              1.
            </button>
            <button
              onClick={() => editor?.chain().focus().setTextAlign('left').run()}
              className={`${styles.toolbarButton} ${editor?.isActive({ textAlign: 'left' }) ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Sola Hizala"
            >
              ←
            </button>
            <button
              onClick={() => editor?.chain().focus().setTextAlign('center').run()}
              className={`${styles.toolbarButton} ${editor?.isActive({ textAlign: 'center' }) ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Ortala"
            >
              ↔
            </button>
            <button
              onClick={() => editor?.chain().focus().setTextAlign('right').run()}
              className={`${styles.toolbarButton} ${editor?.isActive({ textAlign: 'right' }) ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Sağa Hizala"
            >
              →
            </button>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 7: Insert */}
          <div className={styles.toolbarGroup}>
            <button
              onClick={handleLinkToggle}
              className={`${styles.toolbarButton} ${editor?.isActive('link') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Bağlantı"
            >
              🔗
            </button>
            <button
              onClick={() => setShowImageModal(true)}
              className={styles.toolbarButton}
              disabled={toolbarDisabled}
              title="Resim Ekle"
            >
              🖼
            </button>
            <button
              onClick={() => setShowYoutubeModal(true)}
              className={styles.toolbarButton}
              disabled={toolbarDisabled}
              title="YouTube Video"
            >
              ▶
            </button>
            <button
              onClick={insertTable}
              className={styles.toolbarButton}
              disabled={toolbarDisabled}
              title="Tablo Ekle (3x3)"
            >
              ⊞
            </button>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 8: Block */}
          <div className={styles.toolbarGroup}>
            <button
              onClick={() => editor?.chain().focus().toggleBlockquote().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('blockquote') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Alıntı"
            >
              ❝
            </button>
            <button
              onClick={() => editor?.chain().focus().setHorizontalRule().run()}
              className={styles.toolbarButton}
              disabled={toolbarDisabled}
              title="Yatay Çizgi"
            >
              ─
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
              className={`${styles.toolbarButton} ${editor?.isActive('codeBlock') ? styles.active : ''}`}
              disabled={toolbarDisabled}
              title="Kod Bloğu"
            >
              {'{}'}
            </button>
          </div>

          <div className={styles.toolbarSeparator} />

          {/* Group 9: Source */}
          <div className={`${styles.toolbarGroup} ${styles.toolbarGroupRight}`}>
            <button
              onClick={toggleSourceMode}
              className={`${styles.toolbarButton} ${isSourceMode ? styles.active : ''}`}
              title="HTML Kaynak Kodu"
            >
              {'</>'}
            </button>
          </div>
        </div>

        {/* Link input bar */}
        {showLinkInput && (
          <div className={styles.linkInputBar}>
            <input
              type="url"
              className={styles.linkInput}
              placeholder="https://ornek.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyLink()}
              autoFocus
            />
            <button onClick={applyLink} className={styles.linkApplyButton}>Uygula</button>
            <button onClick={removeLink} className={styles.linkRemoveButton}>Kaldır</button>
            <button onClick={() => { setShowLinkInput(false); setLinkUrl(''); }} className={styles.linkCancelButton}>İptal</button>
          </div>
        )}

        {/* Table controls */}
        {isTableActive && !isSourceMode && (
          <div className={styles.tableControls}>
            <span className={styles.tableControlsLabel}>Tablo:</span>
            <button onClick={() => editor?.chain().focus().addRowBefore().run()} className={styles.tableControlButton} title="Üste Satır Ekle">↑ Satır</button>
            <button onClick={() => editor?.chain().focus().addRowAfter().run()} className={styles.tableControlButton} title="Alta Satır Ekle">↓ Satır</button>
            <button onClick={() => editor?.chain().focus().deleteRow().run()} className={styles.tableControlButton} title="Satır Sil">✕ Satır</button>
            <div className={styles.tableControlSeparator} />
            <button onClick={() => editor?.chain().focus().addColumnBefore().run()} className={styles.tableControlButton} title="Sola Sütun Ekle">← Sütun</button>
            <button onClick={() => editor?.chain().focus().addColumnAfter().run()} className={styles.tableControlButton} title="Sağa Sütun Ekle">→ Sütun</button>
            <button onClick={() => editor?.chain().focus().deleteColumn().run()} className={styles.tableControlButton} title="Sütun Sil">✕ Sütun</button>
            <div className={styles.tableControlSeparator} />
            <button onClick={() => editor?.chain().focus().deleteTable().run()} className={styles.tableControlButtonDanger} title="Tabloyu Sil">Tabloyu Sil</button>
          </div>
        )}

        {/* Editor / Source area */}
        {isSourceMode ? (
          <textarea
            className={styles.sourceTextarea}
            value={htmlSource}
            onChange={(e) => setHtmlSource(e.target.value)}
            spellCheck={false}
          />
        ) : (
          <div className={styles.editor}>
            <EditorContent editor={editor} />
          </div>
        )}

        <div className={styles.actions}>
          <button
            onClick={handlePreview}
            className={styles.previewButton}
            disabled={isLoading}
          >
            Önizleme
          </button>
          <button
            onClick={handleSave}
            className={styles.saveButton}
            disabled={isLoading}
          >
            {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Image Modal */}
      <Modal isOpen={showImageModal} onClose={() => { setShowImageModal(false); setImageUrl(''); }} title="Resim Ekle">
        <div className={styles.modalForm}>
          <label className={styles.modalLabel}>Resim URL:</label>
          <input
            type="url"
            className={styles.modalInput}
            placeholder="https://ornek.com/resim.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && insertImage()}
          />
          <div className={styles.modalActions}>
            <button onClick={() => { setShowImageModal(false); setImageUrl(''); }} className={styles.modalCancelButton}>İptal</button>
            <button onClick={insertImage} className={styles.modalConfirmButton} disabled={!imageUrl}>Ekle</button>
          </div>
        </div>
      </Modal>

      {/* YouTube Modal */}
      <Modal isOpen={showYoutubeModal} onClose={() => { setShowYoutubeModal(false); setYoutubeUrl(''); }} title="YouTube Video Ekle">
        <div className={styles.modalForm}>
          <label className={styles.modalLabel}>YouTube URL:</label>
          <input
            type="url"
            className={styles.modalInput}
            placeholder="https://www.youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && insertYoutube()}
          />
          <div className={styles.modalActions}>
            <button onClick={() => { setShowYoutubeModal(false); setYoutubeUrl(''); }} className={styles.modalCancelButton}>İptal</button>
            <button onClick={insertYoutube} className={styles.modalConfirmButton} disabled={!youtubeUrl}>Ekle</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContentManagementPage;
